import { useLayoutEffect } from 'react'
import type { ReactNode } from 'react'

interface BrandProviderProps {
  /** Cor primária do parceiro. `null` (logout / direct) restaura
   *  o fallback do CSS (`--brand-primary` volta a apontar pra
   *  `--app-primary` via inheritance). */
  primaryColor: string | null
  /** Cor secundária do parceiro. `null` ou omitido restaura fallback
   *  (mesma semântica que primary). */
  secondaryColor?: string | null
  children: ReactNode
}

// Aplica branding do parceiro via CSS custom properties.
// Tudo que usa --brand-* atualiza automaticamente. Quando primaryColor
// é null/falsy, REMOVE o override (volta ao default `var(--app-primary)`
// do tokens.css) — garante que pre-provider screens / direct mode não
// herdem cor de parceiro de sessão anterior. Issue paleta APP vs
// PROVIDER (PR #34).
//
// Subset de CSSStyleDeclaration usado pra branding — facilita teste
// (stub no node env, sem precisar de jsdom/happy-dom).
interface CssVarStyle {
  setProperty(name: string, value: string): void
  removeProperty(name: string): string
}

/**
 * Aplica branding do parceiro num root style. Função pura — não
 * depende de `document`. BrandProvider chama com
 * `document.documentElement.style`; testes passam stub.
 *
 * Tratamento uniforme: falsy (`null`, `undefined`, `''`) → remove
 * override. Garante que provider novo sem secondaryColor não herda
 * secondary do provider anterior.
 */
export function applyBranding(
  style: CssVarStyle,
  primaryColor: string | null | undefined,
  secondaryColor: string | null | undefined,
): void {
  if (primaryColor) {
    style.setProperty('--brand-primary', primaryColor)
  } else {
    style.removeProperty('--brand-primary')
  }
  if (secondaryColor) {
    style.setProperty('--brand-secondary', secondaryColor)
  } else {
    style.removeProperty('--brand-secondary')
  }
}

// ============================================================================
// SHIM DE MARCA pro build LEGACY de TV (Chromium 38 — sem custom properties,
// `var()` não existe). Ver o banner grande em
// apps/tv/postcss/legacy-brand-asset-plugin.mjs.
//
// `applyBranding` acima (setProperty/removeProperty em --brand-primary/
// --brand-secondary) é 100% INERTE no Chromium 38: nada consegue LER essas
// custom properties via var() lá (é sintaxe inválida, a declaração inteira é
// descartada). O achatamento de CSS por capability
// (apps/tv/postcss/legacy-flatten-plugin.mjs) extrai TODA declaração que
// depende de --brand-primary/--brand-secondary (~111 no app de TV) pra um
// TEMPLATE com placeholders (`__BRAND_PRIMARY__`/`__BRAND_SECONDARY__`),
// emitido como asset e exposto em `window.__TV_BRAND_TEMPLATE__` — só existe
// no artefato de TV legacy (web/admin/site nunca emitem esse asset).
//
// `renderLegacyBrandCss` é a função PURA (troca de placeholder por hex real,
// com fallback pro roxo do APP quando não há branding de provedor —
// `#7c3aed`/`#6d28d9`, os MESMOS valores de --app-primary/--app-secondary em
// packages/ui/src/tokens.css; DUPLICADO aqui de propósito — Chromium 38 não
// consegue resolver var(--app-primary) pra descobrir o valor em runtime,
// então o literal precisa existir nos DOIS lugares; se um mudar, o outro
// tem que acompanhar). `applyLegacyBrandShim` é o glue que injeta/atualiza
// um `<style>` — chamado do MESMO `useLayoutEffect` de `BrandProvider`, logo
// abaixo, então reage a troca de provider com o MESMO timing que o `var()`
// real teria (sync, antes do paint).
// ============================================================================

/** Mesmos valores de `--app-primary`/`--app-secondary` em
 *  packages/ui/src/tokens.css — MANTER SINCRONIZADO manualmente (ver
 *  banner acima pra por que não dá pra ler do CSS em runtime no Chr38). */
const APP_DEFAULT_PRIMARY = '#7c3aed'
const APP_DEFAULT_SECONDARY = '#6d28d9'

const LEGACY_BRAND_STYLE_ID = 'tv-legacy-brand-shim'

/** Função PURA: troca os placeholders do template pelo hex resolvido (cor
 *  do provedor, ou o default do app se `null`/vazio — mesma semântica
 *  uniforme-falsy de `applyBranding` acima). `replaceAll` é seguro aqui:
 *  os placeholders são texto fixo (`__BRAND_PRIMARY__`/`__BRAND_SECONDARY__`),
 *  nunca aparecem por acidente dentro de um valor CSS real. */
export function renderLegacyBrandCss(
  template: string,
  primaryColor: string | null | undefined,
  secondaryColor: string | null | undefined,
): string {
  return template
    .split('__BRAND_PRIMARY__')
    .join(primaryColor || APP_DEFAULT_PRIMARY)
    .split('__BRAND_SECONDARY__')
    .join(secondaryColor || APP_DEFAULT_SECONDARY)
}

/** Elemento mínimo que o shim manipula — mesmo shape em `getElementById` e
 *  `createElement` de propósito (senão o TS não unifica o tipo de `styleEl`
 *  entre os dois ramos do `if` abaixo). */
interface LegacyStyleElementLike {
  id: string
  textContent: string
}

/** Subset do `document` usado pelo shim — facilita teste (stub, sem DOM).
 *  `insertBefore` (e não só `appendChild`) porque a POSIÇÃO do `<style>` no
 *  head é parte do desenho da cascata — ver `applyLegacyBrandShim`. */
interface LegacyBrandDocument {
  getElementById(id: string): LegacyStyleElementLike | null
  createElement(tag: string): LegacyStyleElementLike
  head: { appendChild(node: unknown): void; insertBefore(node: unknown, ref: unknown): void; firstChild: unknown }
  documentElement: {
    setAttribute(name: string, value: string): void
    removeAttribute(name: string): void
  }
}

/** Atributo que o template usa pra ganhar especificidade — MANTER
 *  SINCRONIZADO com `BRAND_BOOST_ATTR` em
 *  apps/tv/postcss/legacy-brand-asset-plugin.mjs. Se os dois divergirem, o
 *  seletor do template nunca casa e NENHUMA superfície de marca pinta.
 *
 *  Onde isso é verificado (o gate `check-brand-cascade.mjs`, checagem (b)):
 *  no empacotamento de tv-webos/tv-tizen (`scripts/build.mjs`, antes de gerar
 *  .ipk/.wgt) e no job `tv-brand-gates` do CI. NÃO roda no `pnpm build`
 *  genérico — o dist de TV precisa ser o do `build:tv` (--mode tv). */
const BRAND_BOOST_ATTR = 'data-tv-brand'

/** `true` quando a cor resolvida é a MESMA que o build já assou na folha
 *  original. Nesse caso o shim não precisa (nem deve) injetar nada: a folha
 *  resolve a cascata sozinha, com a ordem/especificidade EXATAS do CSS
 *  autoral. Injetar mesmo assim só traria o seletor boostado pra briga sem
 *  nenhum ganho de cor — e o boost, por definição, ganha de regras que no CSS
 *  moderno venceriam a de marca (ex.: `.input.inputError:focus`). Comparação
 *  case-insensitive: o CSS não distingue `#7C3AED` de `#7c3aed`, e a cor do
 *  provedor vem do banco com caixa arbitrária (a marca do HXTV é `#7C3AED`,
 *  literalmente o default — sem esta normalização ela cairia no caminho
 *  boostado à toa). */
function matchesBakedDefault(
  primaryColor: string | null | undefined,
  secondaryColor: string | null | undefined,
): boolean {
  const p = (primaryColor || APP_DEFAULT_PRIMARY).toLowerCase()
  const s = (secondaryColor || APP_DEFAULT_SECONDARY).toLowerCase()
  return p === APP_DEFAULT_PRIMARY && s === APP_DEFAULT_SECONDARY
}

/** Injeta/atualiza o `<style>` do shim. NO-OP se `window.__TV_BRAND_TEMPLATE__`
 *  não existir (todo build que não é o artefato de TV legacy — a esmagadora
 *  maioria: web, admin, site, e o próprio app de TV rodando em engine
 *  moderno/dev) — custo real é 1 leitura de propriedade + comparação, nunca
 *  toca o DOM fora do caso que importa.
 *
 * Quando a cor resolvida É o default assado no build, o shim se DESLIGA
 * (esvazia o <style> e remove o atributo de boost) em vez de reafirmar a
 * mesma cor com especificidade maior — ver `matchesBakedDefault`. Isso
 * também cobre a volta pro default (logout / troca pra provedor sem
 * branding): o atributo sai junto, então nenhuma regra boostada fica ativa.
 *
 * ===== POSIÇÃO NO HEAD É PARTE DO DESENHO (não é detalhe) =====
 * O `<style>` entra como PRIMEIRO filho do `<head>`, não no fim. O boost de
 * especificidade do template é +1 (o mínimo que garante a marca vencer o seu
 * próprio gêmeo na folha — ver o banner em `BRAND_BOOST_ATTR` em
 * apps/tv/postcss/legacy-brand-asset-plugin.mjs), então a regra de marca
 * EMPATA com um competidor não-de-marca um degrau acima dela (o caso real:
 * `.input.inputError:focus` (0,3,0) vs `.input:focus` boostada (0,3,0)).
 * Empate em CSS é decidido por ORDEM: com o shim no INÍCIO, as folhas dos
 * chunks entram depois e ganham o empate — que é exatamente o que o CSS
 * autoral mandava (estado de erro/ativo/foco vence decoração de marca).
 * Com `appendChild` (fim do head) o empate ia pra marca e a borda de erro
 * sumia. Medido no artefato real: 1 inversão com insertBefore, 3 com append.
 *
 * `insertBefore(node, head.firstChild)` com o head VAZIO é equivalente a
 * `appendChild` (o DOM aceita `null` como referência), então não há caso
 * especial a tratar.
 */
export function applyLegacyBrandShim(
  doc: LegacyBrandDocument,
  template: string | undefined,
  primaryColor: string | null | undefined,
  secondaryColor: string | null | undefined,
): void {
  if (!template) return

  if (matchesBakedDefault(primaryColor, secondaryColor)) {
    doc.documentElement.removeAttribute(BRAND_BOOST_ATTR)
    const existing = doc.getElementById(LEGACY_BRAND_STYLE_ID)
    if (existing) existing.textContent = ''
    return
  }

  const css = renderLegacyBrandCss(template, primaryColor, secondaryColor)
  let styleEl = doc.getElementById(LEGACY_BRAND_STYLE_ID)
  if (!styleEl) {
    styleEl = doc.createElement('style')
    styleEl.id = LEGACY_BRAND_STYLE_ID
    doc.head.insertBefore(styleEl, doc.head.firstChild)
  }
  styleEl.textContent = css
  // Depois do textContent: o atributo é o que ATIVA as regras boostadas, então
  // ligá-lo por último evita um frame com o seletor casando uma folha vazia.
  doc.documentElement.setAttribute(BRAND_BOOST_ATTR, '')
}

// useLayoutEffect (não useEffect): sync ANTES do paint, evita flash
// de cor antiga quando provider muda mid-session. Custo zero em SPA.
export function BrandProvider({
  primaryColor,
  secondaryColor,
  children,
}: BrandProviderProps) {
  useLayoutEffect(() => {
    applyBranding(document.documentElement.style, primaryColor, secondaryColor)
    const template = (window as { __TV_BRAND_TEMPLATE__?: string }).__TV_BRAND_TEMPLATE__
    applyLegacyBrandShim(document, template, primaryColor, secondaryColor)
  }, [primaryColor, secondaryColor])

  return <>{children}</>
}
